import { useState, useEffect } from "react";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { BarChart3, Download, FileText, Loader2 } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";
import api from "@/lib/api";
import { toast } from "sonner";

const REPORTS = [
  { key: "stock-summary", label: "Stock Summary" },
  { key: "stock-ledger", label: "Stock Ledger" },
  { key: "low-stock", label: "Low Stock" },
  { key: "login-report", label: "Login Report" },
  { key: "audit-report", label: "Audit Report" },
];

export default function Reports() {
  const [active, setActive] = useState("stock-summary");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (type) => {
    setLoading(true); setActive(type);
    try { const { data } = await api.get(`/reports/${type}`); setData(data); }
    catch { toast.error("Failed to load report"); setData(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { load("stock-summary"); }, []); // eslint-disable-line

  const exportCSV = () => {
    if (!data) return;
    const header = data.columns.join(",");
    const rows = data.rows.map((r) => data.columns.map((c) => `"${(r[c] ?? "").toString().replace(/"/g, '""')}"`).join(","));
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${active}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Exported CSV");
  };

  return (
    <div>
      <PageHeader title="Reports" subtitle="Inventory, movement, audit & login analytics" icon={BarChart3}
        breadcrumb={["Home", "Reports"]}
        actions={
          <Button data-testid="export-csv-btn" onClick={exportCSV} variant="outline">
            <Download className="h-4 w-4 mr-1.5" />Export CSV
          </Button>
        } />

      <div className="flex flex-wrap gap-2 mb-5">
        {REPORTS.map((r) => (
          <button key={r.key} data-testid={`report-${r.key}`} onClick={() => load(r.key)}
            className={`px-4 py-2 rounded-md text-sm font-medium border transition-colors flex items-center gap-2 ${
              active === r.key ? "bg-blue-600 text-white border-blue-600" : "bg-white dark:bg-card text-slate-600 dark:text-muted-foreground border-slate-200 dark:border-border hover:bg-slate-50"
            }`}>
            <FileText className="h-4 w-4" />{r.label}
          </button>
        ))}
      </div>

      <div className="bg-white dark:bg-card border border-slate-200 dark:border-border rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline mr-2" />Loading...</div>
        ) : !data || data.rows.length === 0 ? (
          <div className="py-16 text-center text-slate-400">No data for this report.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 dark:bg-secondary">
                  {data.columns.map((c) => (
                    <th key={c} className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4 whitespace-nowrap">{c.replace(/_/g, " ")}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row, i) => (
                  <tr key={i} className="border-t border-slate-100 dark:border-border hover:bg-slate-50 dark:hover:bg-secondary/50">
                    {data.columns.map((c) => (
                      <td key={c} className="py-2.5 px-4 text-sm text-slate-700 dark:text-foreground whitespace-nowrap">
                        {c === "status" ? <StatusBadge status={row[c]} /> : (row[c] ?? "—").toString()}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <p className="text-xs text-slate-400 mt-3">{data?.rows?.length || 0} rows · CSV export available. PDF/Excel export supported via backend integration.</p>
    </div>
  );
}
